import { Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';

// Import page components (default exports with permissions and layouts)
import LoginPage from '@/pages/login';
import UnAuthorizedPage from '@/pages/UnAuthorizedPage';
import NotFoundPage from '@/pages/404';
import { initI18nNext } from '@/i18n';

// Import all page components
import CustomersPage from '@/pages/kunden/index';
import CustomerDetailsPage from '@/pages/kunden/details';
import TicketsPage from '@/pages/tickets/index';
import TicketDetailsPage from '@/pages/tickets/details';
import VouchersPage from '@/pages/vouchers/index';
import OrderDetailsPage from '@/pages/kunden/bestellungen/index';
import LiftOperatorsPage from '@/pages/bergbahnen/index';
import CouponsPage from '@/pages/coupons/index';
import FaqPage from '@/pages/faq/index';
import TextReplacementsPage from '@/pages/text-ersetzungen/index';
import WebshopsPage from '@/pages/webshops/index';
import AgenciesPage from '@/pages/agenturen/index';
import MailsPage from '@/pages/mails/index';
import BillingsPage from '@/pages/abrechnungen/index';
import ProductionPage from '@/pages/produktion/index';
import HospitalityPage from '@/pages/_hospitality/index';
import HospitalityNewPage from '@/pages/_hospitality/neu';
import HospitalityEditPage from '@/pages/_hospitality/bearbeiten';
import ProductConfigurationPage from '@/pages/_produktkonfiguration/index';
import ProductConfigurationDetailsPage from '@/pages/_produktkonfiguration/details/index';
import SpecialTariffsPage from '@/pages/_spezialtarife/index';
import SpecialTariffsNewPage from '@/pages/_spezialtarife/neu';
import SpecialTariffsDetailsPage from '@/pages/_spezialtarife/details';
import UserManagementPage from '@/pages/_benutzerverwaltung/index';
import MailConfigurationPage from '@/pages/_mail-konfiguration/index';

import '@/styles/global.css';

import 'simplebar-react/dist/simplebar.min.css';
import { PageWithLayout } from '@/pages/_app';
import CashDesksPage from '@/pages/cash-desks';
import CustomerDetailsOld from '@/pages/kunden/detailsOld';
import OrderDetailsOld from '@/pages/kunden/bestellungen/orderDetailsOld';
import { useMemo } from 'react';

const routesConfig = {
    routes: [
        {
            path: '/login',
            component: LoginPage,
        },
        {
            path: '/unauthorized',
            component: UnAuthorizedPage,
        },
        {
            path: '/kunden',
            component: CustomersPage,
        },
        {
            path: '/kunden/:customerId',
            component: CustomerDetailsPage,
        },
        {
            path: 'kunden/details',
            component: CustomerDetailsOld,
        },
        {
            path: '/tickets',
            component: TicketsPage,
        },
        {
            path: '/tickets/details',
            component: TicketDetailsPage,
        },
        {
            path: '/vouchers',
            component: VouchersPage,
        },
        {
            path: '/kunden/:customerId/bestellungen/:orderId',
            component: OrderDetailsPage,
        },

        {
            path: 'bestell-details',
            component: OrderDetailsOld,
        },

        {
            path: '/bergbahnen',
            component: LiftOperatorsPage,
        },
        {
            path: '/coupons',
            component: CouponsPage,
        },
        {
            path: '/faq',
            component: FaqPage,
        },
        {
            path: '/text-ersetzungen',
            component: TextReplacementsPage,
        },
        {
            path: '/webshops',
            component: WebshopsPage,
        },
        {
            path: '/agenturen',
            component: AgenciesPage,
        },
        {
            path: '/cash-desks',
            component: CashDesksPage,
        },
        {
            path: '/mails',
            component: MailsPage,
        },
        {
            path: '/abrechnungen',
            component: BillingsPage,
        },
        {
            path: '/produktion',
            component: ProductionPage,
        },
        {
            path: '/_hospitality',
            component: HospitalityPage,
        },
        {
            path: '/_hospitality/neu',
            component: HospitalityNewPage,
        },
        {
            path: '/_hospitality/bearbeiten',
            component: HospitalityEditPage,
        },
        {
            path: '/_produktkonfiguration',
            component: ProductConfigurationPage,
        },
        {
            path: '/_produktkonfiguration/details',
            component: ProductConfigurationDetailsPage,
        },
        {
            path: '/_spezialtarife',
            component: SpecialTariffsPage,
        },
        {
            path: '/_spezialtarife/neu',
            component: SpecialTariffsNewPage,
        },
        {
            path: '/_spezialtarife/details',
            component: SpecialTariffsDetailsPage,
        },
        {
            path: '/_benutzerverwaltung',
            component: UserManagementPage,
        },
        {
            path: '/_mail-konfiguration',
            component: MailConfigurationPage,
        },
    ],
    redirects: [
        {
            from: '/',
            to: '/kunden',
        },
    ],
    fallback: {
        path: '*',
        component: NotFoundPage,
    },
};

function App() {
    const renderRoute = (
        route: { path: string; component: PageWithLayout },
        key: string
    ) => {
        const Component = route.component;

        // Use the page component's built-in layout
        const layoutComponent = Component.getLayout;

        return (
            <Route
                key={key}
                path={route.path}
                element={
                    layoutComponent ? (
                        layoutComponent(<Component />)
                    ) : (
                        <Component />
                    )
                }
            />
        );
    };

    const i18n = useMemo(() => initI18nNext(), []);

    return (
        <I18nextProvider i18n={i18n}>
            <Routes>
                {/* Redirects */}
                {routesConfig.redirects.map((redirect, index) => (
                    <Route
                        key={`redirect-${index}`}
                        path={redirect.from}
                        element={<Navigate to={redirect.to} replace />}
                    />
                ))}

                {/* All routes */}
                {routesConfig.routes.map((route, index) =>
                    renderRoute(route, `route-${index}`)
                )}

                {/* Fallback route */}
                {renderRoute(routesConfig.fallback, 'fallback')}
            </Routes>
        </I18nextProvider>
    );
}

export default App;
